import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';
import { FiEye, FiEdit2, FiTrash2 } from 'react-icons/fi';
import UserProfileButton from './common/UserProfileButton';
import LogoutButton from './common/LogoutButton';
import receptionBg from '../assets/images/backgrounds/reception-bg.png';
import './TaskManagement/pages/TaskManagement.css';
import './RoutineTasks.css';

const RoutineTasks = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [routines, setRoutines] = useState([]);
    const [users, setUsers] = useState({});
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        assignedTo: [],
        priority: 'medium',
        startDate: '',
        endDate: '',
        recurrenceType: 'daily',
        dayOfWeek: 1,
        dayOfMonth: 1,
        subtasks: [] // [{ title, description }]
    });
    const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(false);
    const [assigneeSearch, setAssigneeSearch] = useState('');
    const assigneeDropdownRef = useRef(null);
    // List filters (applied client-side — the full list is already loaded)
    const [searchTerm, setSearchTerm] = useState('');
    const [recurrenceFilter, setRecurrenceFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [activeFilter, setActiveFilter] = useState('');

    useEffect(() => {
        fetchRoutines();
        fetchUsers();
    }, []);

    const fetchRoutines = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/routine-tasks');
            if (res.data.success) setRoutines(res.data.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await axiosInstance.get('/api/tasks/users');
            if (res.data.success) setUsers(res.data.data || {});
        } catch (e) {
            console.error(e);
        }
    };

    const flatUsers = useMemo(() => {
        if (!users || typeof users !== 'object') return [];
        return Object.values(users).flat().filter(Boolean);
    }, [users]);

    // Filter users by search (for dropdown)
    const filteredUsersByDept = useMemo(() => {
        if (!users || typeof users !== 'object') return {};
        const searchLower = (assigneeSearch || '').trim().toLowerCase();
        const result = {};
        Object.entries(users).forEach(([dept, list]) => {
            if (!Array.isArray(list)) return;
            const filtered = searchLower
                ? list.filter(u =>
                    (u.fullName || '').toLowerCase().includes(searchLower) ||
                    (u.username || '').toLowerCase().includes(searchLower) ||
                    (u.role || '').toLowerCase().includes(searchLower) ||
                    (dept || '').toLowerCase().includes(searchLower))
                : list;
            if (filtered.length) result[dept] = filtered;
        });
        return result;
    }, [users, assigneeSearch]);

    // Apply search + filters to the routine list
    const filteredRoutines = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const now = new Date();
        return routines.filter(r => {
            if (term) {
                const haystack = [
                    r.title,
                    r.description,
                    ...(r.assignedToName || []),
                    r.createdBy?.fullName
                ].filter(Boolean).join(' ').toLowerCase();
                if (!haystack.includes(term)) return false;
            }
            if (recurrenceFilter && ((r.recurrence && r.recurrence.type) || 'daily') !== recurrenceFilter) return false;
            if (priorityFilter && (r.priority || 'medium') !== priorityFilter) return false;
            if (activeFilter) {
                const ended = r.endDate && new Date(r.endDate) < now;
                if (activeFilter === 'active' && ended) return false;
                if (activeFilter === 'ended' && !ended) return false;
            }
            return true;
        });
    }, [routines, searchTerm, recurrenceFilter, priorityFilter, activeFilter]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(e.target)) {
                setAssigneeDropdownOpen(false);
            }
        };
        if (assigneeDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [assigneeDropdownOpen]);

    const openCreate = () => {
        setEditingId(null);
        setFormData({
            title: '',
            description: '',
            assignedTo: [],
            priority: 'medium',
            startDate: new Date().toISOString().slice(0, 10),
            endDate: '',
            recurrenceType: 'daily',
            dayOfWeek: 1,
            dayOfMonth: 1,
            subtasks: []
        });
        setAssigneeDropdownOpen(false);
        setAssigneeSearch('');
        setShowModal(true);
    };

    const openEdit = (r) => {
        setEditingId(r._id);
        setFormData({
            title: r.title,
            description: r.description,
            assignedTo: (r.assignedTo || []).map(a => (typeof a === 'object' ? a._id : a)),
            priority: r.priority || 'medium',
            startDate: r.startDate ? new Date(r.startDate).toISOString().slice(0, 10) : '',
            endDate: r.endDate ? new Date(r.endDate).toISOString().slice(0, 10) : '',
            recurrenceType: (r.recurrence && r.recurrence.type) || 'daily',
            dayOfWeek: (r.recurrence && r.recurrence.dayOfWeek != null) ? r.recurrence.dayOfWeek : 1,
            dayOfMonth: (r.recurrence && r.recurrence.dayOfMonth != null) ? r.recurrence.dayOfMonth : 1,
            subtasks: (r.subtasks || []).map(s => ({ title: s.title || '', description: s.description || '' }))
        });
        setAssigneeDropdownOpen(false);
        setAssigneeSearch('');
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title.trim() || !formData.description.trim() || !formData.assignedTo.length || !formData.startDate) {
            alert('Please fill title, description, assignees, and start date.');
            return;
        }
        const payload = {
            title: formData.title.trim(),
            description: formData.description.trim(),
            assignedTo: formData.assignedTo,
            priority: formData.priority,
            startDate: formData.startDate,
            endDate: formData.endDate || undefined,
            recurrence: {
                type: formData.recurrenceType,
                dayOfWeek: formData.recurrenceType === 'weekly' ? formData.dayOfWeek : undefined,
                dayOfMonth: formData.recurrenceType === 'monthly' ? formData.dayOfMonth : undefined
            },
            subtasks: (formData.subtasks || []).filter(s => (s.title || '').trim()).map(s => ({ title: s.title.trim(), description: (s.description || '').trim() }))
        };
        try {
            if (editingId) {
                await axiosInstance.put(`/api/routine-tasks/${editingId}`, payload);
                alert('Routine updated.');
            } else {
                await axiosInstance.post('/api/routine-tasks', payload);
                alert('Routine created.');
            }
            setShowModal(false);
            fetchRoutines();
        } catch (err) {
            alert(err.response?.data?.message || 'Request failed');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this routine? Pending tasks generated from it will also be removed (completed ones are kept).')) return;
        try {
            await axiosInstance.delete(`/api/routine-tasks/${id}`);
            fetchRoutines();
        } catch (err) {
            alert(err.response?.data?.message || 'Delete failed');
        }
    };

    const toggleAssignee = (userId) => {
        setFormData(prev => ({
            ...prev,
            assignedTo: prev.assignedTo.includes(userId)
                ? prev.assignedTo.filter(id => id !== userId)
                : [...prev.assignedTo, userId]
        }));
    };

    const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
        <div className="task-management-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="task-management-content">
                <div className="task-header">
                    <img src="/logo.png" alt="Logo" className="app-logo" />
                    <div className="header-text">
                        <h1>Routine Tasks</h1>
                        <p>Create tasks on a schedule (start / end)</p>
                    </div>
                    <div className="header-actions">
                        <UserProfileButton />
                        <button type="button" className="btn-back" onClick={() => navigate('/tasks')}>← Tasks</button>
                        <LogoutButton variant="secondary" />
                    </div>
                </div>

            <div className="task-content" style={{ padding: '20px' }}>
                <div className="toolbar">
                    <button type="button" className="btn-primary" onClick={openCreate}>+ New Routine</button>
                </div>

                <div className="task-controls">
                    <input
                        type="text"
                        placeholder="Search by title, description, assignee..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                    <select value={recurrenceFilter} onChange={e => setRecurrenceFilter(e.target.value)} className="filter-select">
                        <option value="">All Recurrence</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                    </select>
                    <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="filter-select">
                        <option value="">All Priority</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                    </select>
                    <select value={activeFilter} onChange={e => setActiveFilter(e.target.value)} className="filter-select">
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="ended">Ended</option>
                    </select>
                    <span className="routine-filter-count">
                        {filteredRoutines.length} of {routines.length} routine(s)
                    </span>
                </div>

                {loading ? (
                    <div className="loading-state">Loading routine tasks...</div>
                ) : (
                    <div className="tasks-table-container">
                        <table className="tasks-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Title</th>
                                    <th>Assignees</th>
                                    <th>Created By</th>
                                    <th>Recurrence</th>
                                    <th>Start</th>
                                    <th>End</th>
                                    <th>Subtasks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRoutines.length === 0 ? (
                                    <tr><td colSpan={8} className="empty-cell">
                                        {routines.length === 0
                                            ? 'No routine tasks. Create one to auto-generate tasks on a schedule.'
                                            : 'No routines match the current filters.'}
                                    </td></tr>
                                ) : (
                                    filteredRoutines.map(r => (
                                        <tr key={r._id}>
                                            <td className="td-actions-icons">
                                                <div className="row-action-icons">
                                                    <button type="button" onClick={() => openEdit(r)} className="btn-icon btn-icon-view" title="View / Edit" aria-label="View">
                                                        <FiEye size={18} />
                                                    </button>
                                                    <button type="button" onClick={() => openEdit(r)} className="btn-icon btn-icon-edit-inline" title="Edit" aria-label="Edit">
                                                        <FiEdit2 size={18} />
                                                    </button>
                                                    {(user?.role === 'system_admin' ||
                                                        String(r.createdBy?._id || r.createdBy) === String(user?._id || user?.id)) && (
                                                        <button type="button" onClick={() => handleDelete(r._id)} className="btn-icon btn-icon-delete" title="Delete" aria-label="Delete">
                                                            <FiTrash2 size={18} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td><strong>{r.title}</strong></td>
                                            <td className="routine-cell-assignees">{(r.assignedToName || []).slice(0, 2).join(', ')}{(r.assignedToName || []).length > 2 ? '…' : ''}</td>
                                            <td>{r.createdBy?.fullName || r.createdBy?.username || '—'}</td>
                                            <td>
                                                <span className="routine-recurrence-badge">{(r.recurrence && r.recurrence.type) || 'daily'}</span>
                                                {r.recurrence && r.recurrence.type === 'weekly' && r.recurrence.dayOfWeek != null && ` (${dayNames[r.recurrence.dayOfWeek]})`}
                                                {r.recurrence && r.recurrence.type === 'monthly' && r.recurrence.dayOfMonth != null && ` (day ${r.recurrence.dayOfMonth})`}
                                            </td>
                                            <td>{formatDate(r.startDate)}</td>
                                            <td>{formatDate(r.endDate)}</td>
                                            <td>{(r.subtasks && r.subtasks.length) || 0}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content modal-large routine-modal" onClick={e => e.stopPropagation()}>
                        <div className="routine-modal-header">
                            <h2>{editingId ? 'Edit Routine' : 'New Routine Task'}</h2>
                            <button type="button" className="routine-modal-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
                        </div>
                        <form onSubmit={handleSubmit} className="routine-modal-form">
                            <section className="routine-modal-section">
                                <h3 className="routine-section-title">Details</h3>
                                <div className="form-group">
                                    <label>Title *</label>
                                    <input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="e.g. Daily standup" required />
                                </div>
                                <div className="form-group">
                                    <label>Description *</label>
                                    <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={3} placeholder="What this routine is for..." required />
                                </div>
                                <div className="routine-form-row">
                                    <div className="form-group routine-assignee-field" ref={assigneeDropdownRef}>
                                        <label>Assignees *</label>
                                        <div
                                            className="routine-assignee-trigger"
                                            onClick={() => setAssigneeDropdownOpen(prev => !prev)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAssigneeDropdownOpen(prev => !prev); } }}
                                            aria-expanded={assigneeDropdownOpen}
                                            aria-haspopup="listbox"
                                        >
                                            <span className="routine-assignee-trigger-text">
                                                {formData.assignedTo.length === 0
                                                    ? 'Select assignees...'
                                                    : `${formData.assignedTo.length} assignee(s) selected`}
                                            </span>
                                            <span className="routine-assignee-trigger-icon">{assigneeDropdownOpen ? '▲' : '▼'}</span>
                                        </div>
                                        {formData.assignedTo.length === 0 && (
                                            <small className="routine-assignee-hint">Please select at least one user</small>
                                        )}
                                        {assigneeDropdownOpen && (
                                            <div className="routine-assignee-dropdown">
                                                <input
                                                    type="text"
                                                    className="routine-assignee-search"
                                                    placeholder="Search by name, role, or department..."
                                                    value={assigneeSearch}
                                                    onChange={e => setAssigneeSearch(e.target.value)}
                                                    onClick={e => e.stopPropagation()}
                                                />
                                                <div className="routine-assignee-list">
                                                    {Object.keys(filteredUsersByDept).length === 0 ? (
                                                        <div className="routine-assignee-empty">No users match</div>
                                                    ) : (
                                                        Object.entries(filteredUsersByDept).map(([dept, list]) => (
                                                            <div key={dept} className="routine-assignee-dept">
                                                                <div className="routine-assignee-dept-name">{dept}</div>
                                                                {list.map(u => (
                                                                    <label key={u._id} className="routine-assignee-option">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={formData.assignedTo.includes(u._id)}
                                                                            onChange={() => toggleAssignee(u._id)}
                                                                        />
                                                                        <span className="routine-assignee-option-name">{u.fullName || u.username}</span>
                                                                        {u.role && <span className="routine-assignee-option-role">{u.role}</span>}
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="form-group routine-priority-field">
                                        <label>Priority</label>
                                        <select value={formData.priority} onChange={e => setFormData({ ...formData, priority: e.target.value })}>
                                            <option value="low">Low</option>
                                            <option value="medium">Medium</option>
                                            <option value="high">High</option>
                                            <option value="urgent">Urgent</option>
                                        </select>
                                    </div>
                                </div>
                            </section>
                            <section className="routine-modal-section">
                                <h3 className="routine-section-title">Schedule</h3>
                                <div className="routine-form-row routine-dates-row">
                                    <div className="form-group">
                                        <label>Start date *</label>
                                        <input type="date" value={formData.startDate} onChange={e => setFormData({ ...formData, startDate: e.target.value })} required />
                                    </div>
                                    <div className="form-group">
                                        <label>End date (optional)</label>
                                        <input type="date" value={formData.endDate} onChange={e => setFormData({ ...formData, endDate: e.target.value })} />
                                    </div>
                                </div>
                                <div className="form-group routine-recurrence-row">
                                    <label>Recurrence</label>
                                    <div className="routine-recurrence-controls">
                                        <select value={formData.recurrenceType} onChange={e => setFormData({ ...formData, recurrenceType: e.target.value })} className="routine-recurrence-select">
                                            <option value="daily">Daily</option>
                                            <option value="weekly">Weekly</option>
                                            <option value="monthly">Monthly</option>
                                        </select>
                                        {formData.recurrenceType === 'weekly' && (
                                            <select value={formData.dayOfWeek} onChange={e => setFormData({ ...formData, dayOfWeek: Number(e.target.value) })} className="routine-recurrence-extra">
                                                {dayNames.map((name, i) => <option key={i} value={i}>{name}</option>)}
                                            </select>
                                        )}
                                        {formData.recurrenceType === 'monthly' && (
                                            <span className="routine-recurrence-extra-wrap">
                                                <label className="routine-recurrence-extra-label">Day of month</label>
                                                <input type="number" min={1} max={31} value={formData.dayOfMonth} onChange={e => setFormData({ ...formData, dayOfMonth: Number(e.target.value) || 1 })} className="routine-recurrence-extra routine-recurrence-day" />
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </section>
                            <section className="routine-modal-section routine-subtasks-section">
                                <h3 className="routine-section-title">Subtasks (optional)</h3>
                                <p className="routine-subtasks-hint">When a task is generated from this routine, these will be created as child tasks.</p>
                                <div className="routine-subtasks-list">
                                    {(formData.subtasks || []).map((st, idx) => (
                                        <div key={idx} className="routine-subtask-row">
                                            <input
                                                type="text"
                                                placeholder="Subtask title"
                                                value={st.title}
                                                onChange={e => {
                                                    const next = [...(formData.subtasks || [])];
                                                    next[idx] = { ...next[idx], title: e.target.value };
                                                    setFormData({ ...formData, subtasks: next });
                                                }}
                                                className="routine-subtask-title"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Description (optional)"
                                                value={st.description || ''}
                                                onChange={e => {
                                                    const next = [...(formData.subtasks || [])];
                                                    next[idx] = { ...next[idx], description: e.target.value };
                                                    setFormData({ ...formData, subtasks: next });
                                                }}
                                                className="routine-subtask-desc"
                                            />
                                            <button type="button" className="routine-subtask-remove" onClick={() => setFormData({ ...formData, subtasks: (formData.subtasks || []).filter((_, i) => i !== idx) })} title="Remove" aria-label="Remove subtask">×</button>
                                        </div>
                                    ))}
                                </div>
                                <button type="button" className="routine-add-subtask-btn" onClick={() => setFormData({ ...formData, subtasks: [...(formData.subtasks || []), { title: '', description: '' }] })}>+ Add subtask</button>
                            </section>
                            <div className="routine-modal-actions">
                                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button>
                                <button type="submit" className="btn-primary">{editingId ? 'Update routine' : 'Create routine'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

export default RoutineTasks;
